import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { keyframes, styled } from "@mui/system";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useJobColors } from "../../hooks/useJobColors";
import { useRoleColors, useRoleGroupColors } from "../../hooks/useRoleColors";
import { INK, NEUTRAL } from "../../theme/tokens";
import { shiftJobColor } from "../../utils/categoricalColors";
import type { NurseFullRole, ShiftStaffTimes } from "../../utils/models";
import { getShortRole } from "../../utils/utilFunctions";
import { MAX_OVERLAP_MINUTES_FOR_SAME_ROW } from "../DayViewGaps/utils";

const slideInLeft = keyframes`
	from {
		transform: translateX(-100%);
		opacity: 0;
	}
	to {
		transform: translateX(0);
		opacity: 1;
	}
`;

const ShiftRowTypography = styled(Typography)(({ theme }) => ({
	fontWeight: "700",
	color: INK.SECONDARY,
	whiteSpace: "nowrap",
	fontSize: "14px",
	overflow: "hidden",
	textOverflow: "ellipsis",
}));

const AnimatedShiftBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== "animationDelay",
})<{ animationDelay: number }>(({ animationDelay }) => ({
	cursor: "pointer",
	display: "flex",
	alignItems: "center",
	borderRadius: "2px",
	overflow: "hidden",
	padding: "8px",
	animation: `${slideInLeft} 0.6s ease-out forwards`,
	animationDelay: `${animationDelay}ms`,
	transform: "translateX(-100%)",
	opacity: 0,
}));

// One grid column is one hour; below this many a bar has no room for its role label.
const MIN_COLUMNS_FOR_ROLE_LABEL = 3;

interface IProps {
	shiftStaffTimes: ShiftStaffTimes[];
	shiftStart: DateTime;
	startTime: DateTime;
	endTime: DateTime;
	hoursRange: number;
	onShiftClick: (staffTimes: ShiftStaffTimes) => void;
	selectedRoles: (NurseFullRole | "All")[];
	searchText?: string | null;
	animationDelay?: number;
}

export const ShiftRow: FC<IProps> = ({
	shiftStaffTimes,
	shiftStart,
	startTime,
	endTime,
	hoursRange,
	onShiftClick,
	selectedRoles,
	searchText,
	animationDelay = 0,
}) => {
	const roleColor = useRoleColors();
	const roleGroupColor = useRoleGroupColors();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const jobColor = useJobColors();
	const { settings } = useUserSettingsContext();

	const calculateGridPosition = (shiftStartTime: DateTime, shiftEndTime: DateTime) => {
		const totalColumns = Math.ceil(hoursRange);

		const startHours = shiftStartTime.diff(shiftStart, "hours").as("hours");
		const startColumn = Math.max(1, Math.floor(startHours) + 1);

		const durationHours = shiftEndTime.diff(shiftStartTime, "hours").as("hours");
		const adjustedDuration = durationHours < 0 ? durationHours + 24 : durationHours;
		const columnSpan = Math.max(1, Math.ceil(adjustedDuration));

		const maxEndColumn = totalColumns + 1;
		const actualColumnSpan = Math.min(columnSpan, maxEndColumn - startColumn);

		return {
			startColumn,
			columnSpan: actualColumnSpan,
			totalColumns,
		};
	};

	const isFilteredShift = (shiftTime: ShiftStaffTimes): boolean => {
		const shiftStartTimePlusHour = shiftTime.startTime.plus({ hours: 1 });
		const shiftEndTimeMinusHour = shiftTime.endTime.minus({ hours: 1 });

		return (
			((shiftStartTimePlusHour >= startTime && shiftStartTimePlusHour <= endTime) ||
				(shiftEndTimeMinusHour >= startTime && shiftEndTimeMinusHour <= endTime)) &&
			(!selectedRoles.length || selectedRoles.includes(shiftTime.role)) &&
			(!searchText || !!shiftTime.names?.includes(searchText))
		);
	};

	const doShiftsOverlap = (shift1: ShiftStaffTimes, shift2: ShiftStaffTimes): boolean => {
		const overlapStartSeconds = Math.max(shift1.actualStartTime.toSeconds(), shift2.actualStartTime.toSeconds());
		const overlapEndSeconds = Math.min(shift1.actualEndTime.toSeconds(), shift2.actualEndTime.toSeconds());
		const overlapMinutes = (overlapEndSeconds - overlapStartSeconds) / 60;
		return overlapMinutes > MAX_OVERLAP_MINUTES_FOR_SAME_ROW;
	};

	const hasEnoughSpaceForRole = (columnSpan: number, shiftTime: ShiftStaffTimes): boolean => {
		const hasRequestedCount = shiftTime.requestedCount && shiftTime.requestedCount > 0;
		const adjustedMinWidth = MIN_COLUMNS_FOR_ROLE_LABEL + (hasRequestedCount ? 1 : 0);

		return columnSpan >= adjustedMinWidth;
	};

	const shiftGroups: ShiftStaffTimes[][] = shiftStaffTimes.reduce((groups, shift) => {
		const groupIndex = groups.findIndex((group) => group.every((existingShift) => !doShiftsOverlap(shift, existingShift)));

		if (groupIndex !== -1) {
			return groups.map((group, index) => (index === groupIndex ? [...group, shift] : group));
		}

		return groups.concat([[shift]]);
	}, [] as ShiftStaffTimes[][]);

	const totalColumns = Math.ceil(hoursRange);
	const gridTemplateColumns = `repeat(${totalColumns}, 1fr)`;

	const bgColor = (shiftTime: ShiftStaffTimes) => {
		const jobTone = shiftJobColor(jobColor, shiftTime.jobId);
		if (jobTone) {
			return jobTone.fill;
		}
		if (settings.groupRoles && shiftTime.roleGroup) {
			return roleGroupColor(shiftTime.roleGroup, chosenDepartment.role_groups).fill;
		}

		return roleColor(shiftTime.role).fill;
	};

	const title = (shiftTime: ShiftStaffTimes) => {
		if (shiftTime.jobName) {
			return shiftTime.jobName;
		}
		if (settings.groupRoles && shiftTime.roleGroup) {
			return shiftTime.roleGroup;
		}

		return getShortRole(shiftTime.role);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
			}}
		>
			{shiftGroups.map((group, groupIndex) => (
				<Box
					key={`group-${groupIndex.toString()}`}
					sx={{
						mb: 0.2,
						height: "16px",
					}}
				>
					<Grid
						container
						sx={{
							display: "grid",
							gridTemplateColumns,
							height: "100%",
							gap: "2px",
						}}
					>
						{(() => {
							const sorted = [...group].sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
							const positions = sorted.map((s) => calculateGridPosition(s.startTime, s.endTime));

							for (let i = 0; i < positions.length - 1; i++) {
								const currentEnd = positions[i].startColumn + positions[i].columnSpan;
								if (currentEnd > positions[i + 1].startColumn) {
									positions[i].columnSpan = Math.max(1, positions[i + 1].startColumn - positions[i].startColumn);
								}
							}

							return sorted.map((shiftTime, index) => {
								const { startColumn, columnSpan } = positions[index];
								const calculatedDelay = animationDelay + groupIndex * 100 + index * 150;
								const showRole = hasEnoughSpaceForRole(columnSpan, shiftTime);

								return (
									<Tooltip
										key={`shift-${index.toString()}`}
										title={`${shiftTime.actualStartTime.setZone(timezone).toFormat("h:mma")}-${shiftTime.actualEndTime.setZone(timezone).toFormat("h:mma")}`}
									>
										<AnimatedShiftBox
											animationDelay={calculatedDelay}
											sx={{
												gridColumn: `${startColumn} / span ${columnSpan}`,
												gridRow: 1,
												backgroundColor: isFilteredShift(shiftTime) ? bgColor(shiftTime) : NEUTRAL[300],
												justifyContent: "center",
											}}
											onClick={() => onShiftClick(shiftTime)}
										>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: "3px",
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
												}}
											>
												{showRole && (
													<ShiftRowTypography
														sx={{
															fontWeight: "700",
															color: INK.SECONDARY,
															whiteSpace: "nowrap",
															fontSize: "14px",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
													>
														{title(shiftTime)}
													</ShiftRowTypography>
												)}
												<ShiftRowTypography
													sx={{
														fontWeight: "700",
														color: INK.SECONDARY,
														whiteSpace: "nowrap",
														fontSize: "14px",
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}
												>
													{shiftTime.count > 0 && shiftTime.count}
												</ShiftRowTypography>
												{shiftTime.requestedCount && (
													<ShiftRowTypography
														sx={{
															fontWeight: "700",
															color: INK.SECONDARY,
															whiteSpace: "nowrap",
															fontSize: "14px",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
													>
														+{shiftTime.requestedCount}
													</ShiftRowTypography>
												)}
											</Box>
										</AnimatedShiftBox>
									</Tooltip>
								);
							});
						})()}
					</Grid>
				</Box>
			))}
		</Box>
	);
};
